import React from 'react'
import { observer } from 'mobx-react-lite'
import classNames from 'classnames'
import moment from 'moment'
import { IconName } from '@fortawesome/fontawesome-svg-core'

// evertel
import { Card, CardBody, Row, Col, Text, Icon } from '@evertel/web/ui'
import { APIDataDocumentSchema, DocumentTileField } from '@evertel/types'
import { DocumentIcon } from './DocumentIcon'

// same as the mobile tile's footer
const DATE_FORMAT = 'DD MMM YYYY HH:mm'

interface DocumentTileBodyProps {
    name?: string
    schema?: APIDataDocumentSchema
    departmentShortName?: string
    /** The template's room tile configuration, resolved to label/value pairs. Without it the tile renders its default layout */
    fields?: DocumentTileField[]
    isArchived?: boolean
    /**
     * Who owns the document, already resolved for display ('Me' for the current user). An empty
     * string means the document has no owner; leave it undefined to leave the owner out entirely
     */
    ownerName?: string
    updatedDate?: moment.MomentInput
    onClick?: () => void
    className?: string
}

const DocumentTileBody: React.FC<DocumentTileBodyProps> = observer(({
    name,
    schema,
    departmentShortName,
    fields,
    isArchived,
    ownerName,
    updatedDate,
    onClick,
    className
}) => {

    const subtext = `${schema?.name ?? ''}${departmentShortName ? ` (${departmentShortName})` : ''}`
    const hasFields = !!fields?.length
    const hasOwner = ownerName !== undefined

    return (
        <Card
            className={classNames('document-tile', className)}
            onClick={onClick}
            as={onClick ? 'button' : undefined}
            style={{
                borderLeftColor: schema?.color,
                borderLeftWidth: 5
            }}>
            <CardBody>
                <Row>
                    <Col
                        xs={12}
                        className="p-0">
                        <Row>
                            {/* the name is always shown in full, wrapping as far as it needs to */}
                            <Text
                                bold
                                className="document-tile-name">
                                {name || 'Untitled Document'}
                            </Text>
                        </Row>
                        <Row className="mt-2">
                            <DocumentIcon
                                schema={schema}
                            />
                            <Text
                                size={12}
                                className="ms-2"
                                style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                                title={subtext}>
                                {subtext}
                            </Text>
                        </Row>
                        {(hasFields) &&
                            <div className="document-tile-fields mt-3">
                                {fields.map((field, index) => (
                                    <TileField
                                        key={field.fieldId ?? index}
                                        field={field}
                                    />
                                ))}
                            </div>
                        }
                        {/* owner, then archived in place of the last-updated date, as on mobile */}
                        {(hasOwner || isArchived || !!updatedDate) &&
                            <div className="document-tile-footer mt-3">
                                {(hasOwner) &&
                                    <MetaBadge
                                        icon="user"
                                        text={ownerName || 'No owner'}
                                        variant={(ownerName) ? 'default' : 'danger'}
                                        truncate
                                    />
                                }
                                {(isArchived)
                                    ? <MetaBadge
                                        text="Archived"
                                        variant="archived"
                                    />
                                    : (!!updatedDate) &&
                                        <MetaBadge
                                            icon="clock"
                                            text={moment(updatedDate).format(DATE_FORMAT)}
                                            title="Last updated"
                                        />
                                }
                            </div>
                        }
                    </Col>
                </Row>
            </CardBody>
        </Card>
    )
})

interface MetaBadgeProps {
    text: string
    icon?: IconName
    title?: string
    variant?: 'default' | 'archived' | 'danger'
    /** may shrink below its content width, truncating its text rather than wrapping */
    truncate?: boolean
}

const MetaBadge: React.FC<MetaBadgeProps> = ({
    text,
    icon,
    title,
    variant = 'default',
    truncate
}) => (
    <span
        className={classNames('document-tile-meta', `document-tile-meta--${variant}`, { 'document-tile-meta--truncate': truncate })}
        title={title ? `${title}: ${text}` : text}>
        {(!!icon) &&
            <Icon
                name={icon}
                size={12}
                aria-hidden="true"
            />
        }
        <span className="document-tile-meta-text">
            {text}
        </span>
    </span>
)

interface TileFieldProps {
    field: DocumentTileField
}

const TileField: React.FC<TileFieldProps> = ({ field }) => {

    const value = Array.isArray(field.value) ? field.value.join(', ') : field.value

    if (!value) return null

    return (
        <div className="document-tile-field">
            <Text
                tag="span"
                bold>
                {field.label}:{' '}
            </Text>
            <Text
                tag="span"
                color="muted">
                {value}
            </Text>
        </div>
    )
}

export { DocumentTileBody }
